import { useState } from 'react';
import { useGallery } from '../hooks/useGallery';
import { useGallerySelection } from '../hooks/useGallerySelection';
import { usePendingMediaPoller } from '../hooks/usePendingMediaPoller';
import { useMediaDownload } from '../hooks/useMediaDownload';
import type { DownloadableItem } from '../hooks/useMediaDownload';
import { apiGetRaw } from '../api/client';
import { SpeechTabs } from './SpeechTabs';
import { FocusedAudio } from './FocusedAudio';
import { GalleryToolbar } from './GalleryToolbar';
import { NewSpeechDialog } from './NewSpeechDialog';
import { LoadMoreSentinel } from './LoadMoreSentinel';
import {
  categoryOf,
  type SpeechType,
} from '../hooks/speechCategory';

const API_BASE = import.meta.env.VITE_API_BASE as string;

interface SpeechItem {
  id: string;
  title: string;
  prompt: string;
  voice: string;
  mode: string;
  duration_ms: number | null;
  koji_codename: string;
  created_at: number;
  r2_key: string | null;
  signedUrl: string | null;
  preset_id?: string | null;
  preset_name?: string | null;
  // Stage 25: async job state.
  status: 'submitted' | 'in_progress' | 'ready' | 'failed' | string;
  error_message: string | null;
  completed_at: number | null;
}

function statusLabel(status: string): string {
  switch (status) {
    case 'submitted':
      return 'Queued';
    case 'in_progress':
      return 'Generating…';
    case 'ready':
      return 'Ready';
    case 'failed':
      return 'Failed';
    default:
      return status;
  }
}

function absolute(url: string): string {
  return url.startsWith('http') ? url : `${API_BASE}${url}`;
}

function fmtDuration(ms: number | null | undefined): string {
  if (!ms || !Number.isFinite(ms)) return '—';
  const s = Math.round(ms / 1000);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}m${r.toString().padStart(2, '0')}s` : `${r}s`;
}

function slugify(s: string, fallback: string): string {
  const base = s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return base || fallback;
}

async function downloadSignedUrl(
  signedUrl: string,
  filename: string,
): Promise<void> {
  const res = await fetch(signedUrl);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

function emptyMessageFor(type: SpeechType): string {
  if (type === 'script')
    return 'No voiceovers yet. Ask the assistant to generate speech.';
  if (type === 'convo')
    return 'No per-turn chat speech has been saved yet.';
  return 'No speech recordings of any kind yet.';
}

export function SpeechGallery() {
  const [type, setType] = useState<SpeechType>('script');
  const endpoint = `/api/tools/speech?type=${type}`;
  const { items, loading, error, deleteItem, refresh, loadMore, hasMore } =
    useGallery<SpeechItem>(endpoint);
  const selection = useGallerySelection(endpoint);
  // Stage 27: selection-driven download (1 file or ZIP via POST { ids }).
  // The existing per-card Download button and per-tab "Download all (ZIP)"
  // button are unchanged below — this only adds the toolbar affordance.
  const download = useMediaDownload({
    zipEndpoint: '/api/tools/speech/zip',
    kind: 'speech',
  });
  const [busyId, setBusyId] = useState<string | null>(null);
  const [busyZip, setBusyZip] = useState<boolean>(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [localErr, setLocalErr] = useState<string | null>(null);

  usePendingMediaPoller('/api/tools/speech/pending', refresh);

  const allIds = items.map((i) => i.id);

  const selectedDownloadItems: DownloadableItem[] = items
    .filter((c) => selection.selected.has(c.id))
    .map((c) => ({
      id: c.id,
      signedUrl: c.signedUrl,
      status: c.status,
      title: c.title,
      koji_codename: c.koji_codename,
      mime_type: 'audio/wav',
      mode: c.mode,
    }));

  const onDownload = async (c: SpeechItem): Promise<void> => {
    if (!c.signedUrl) return;
    setBusyId(c.id);
    setLocalErr(null);
    try {
      const src = absolute(c.signedUrl);
      const category = categoryOf(c.mode);
      const name = slugify(c.title || c.koji_codename, c.id.slice(0, 8));
      await downloadSignedUrl(src, `${category}_${name}.wav`);
    } catch (e) {
      setLocalErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  const onDownloadZip = async (): Promise<void> => {
    setBusyZip(true);
    setLocalErr(null);
    try {
      const res = await apiGetRaw(`/api/tools/speech/zip?type=${type}`);
      const blob = await res.blob();
      const today = new Date().toISOString().slice(0, 10);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `speech-${type}-${today}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      setLocalErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyZip(false);
    }
  };

  return (
    <section className="gallery gallery-speech">
      <GalleryToolbar
        onNew={() => setDialogOpen(true)}
        newLabel="+ New speech clip"
        selection={selection}
        allIds={allIds}
        download={{
          selectedItems: selectedDownloadItems,
          downloadOne: download.downloadOne,
          downloadZip: download.downloadZip,
          busy: download.busy,
          error: download.error,
        }}
        extras={
          <>
            <SpeechTabs value={type} onChange={setType} />
            <button
              type="button"
              onClick={() => void onDownloadZip()}
              disabled={busyZip || loading || items.length === 0}
              title={
                items.length === 0
                  ? 'No clips to bundle in this tab'
                  : `Download all ${type.toUpperCase()} clips as a ZIP`
              }
            >
              {busyZip
                ? 'Bundling…'
                : `⬇ Download all ${type.toUpperCase()} (ZIP)`}
            </button>
          </>
        }
      />
      {loading && items.length === 0 ? (
        <div className="muted">Loading clips…</div>
      ) : error ? (
        <div className="status-missing">Error: {error}</div>
      ) : items.length === 0 ? (
        <p className="muted gallery-empty">{emptyMessageFor(type)}</p>
      ) : (
        <ul className="track-list">
          {items.map((c) => {
            const isSelected = selection.selected.has(c.id);
            const isReady = c.status === 'ready' && c.signedUrl;
            const isFailed = c.status === 'failed';
            const isPending =
              c.status === 'submitted' || c.status === 'in_progress';
            return (
              <li
                key={c.id}
                className={
                  'track-row' +
                  (isSelected ? ' track-row-selected' : '') +
                  (isPending ? ' gallery-card-pending' : '') +
                  (isFailed ? ' gallery-card-failed' : '')
                }
              >
                {selection.selectMode ? (
                  <label className="gallery-select-label">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => selection.toggleSelected(c.id)}
                      aria-label={`Select ${c.title || c.koji_codename}`}
                    />
                  </label>
                ) : !isReady ? (
                  <div className="gallery-status-badge" aria-live="polite">
                    {statusLabel(c.status)}
                  </div>
                ) : null}
                <div className="track-meta">
                  <div className="track-title">
                    {c.title || c.koji_codename}
                  </div>
                  <div className="muted small">
                    {c.preset_name ? (
                      <>
                        🎛 <strong>{c.preset_name}</strong> ·{' '}
                      </>
                    ) : null}
                    {c.voice} · {c.mode} · {fmtDuration(c.duration_ms)} ·{' '}
                    {c.koji_codename}
                  </div>
                  {isReady ? (
                    <FocusedAudio
                      surface={
                        categoryOf(c.mode) === 'CONVO' ? 'tts' : 'speech'
                      }
                      className="track-audio"
                      controls
                      preload="none"
                      src={absolute(c.signedUrl as string)}
                    />
                  ) : null}
                  {isPending ? (
                    <div className="muted small">~15 s</div>
                  ) : null}
                  {isFailed && c.error_message ? (
                    <div
                      className="status-missing small"
                      title={c.error_message}
                    >
                      {c.error_message.slice(0, 100)}
                      {c.error_message.length > 100 ? '…' : ''}
                    </div>
                  ) : null}
                </div>
                {!selection.selectMode ? (
                  <div className="track-actions">
                    {isReady ? (
                      <button
                        type="button"
                        onClick={() => void onDownload(c)}
                        disabled={busyId === c.id}
                        aria-label={`Download ${c.title || c.koji_codename}`}
                        title="Download as .wav (category-prefixed filename)"
                      >
                        {busyId === c.id ? 'Downloading…' : 'Download'}
                      </button>
                    ) : null}
                    <button
                      className="btn-danger small"
                      onClick={() => {
                        if (confirm('Delete this clip?'))
                          void deleteItem(c.id);
                      }}
                    >
                      Delete
                    </button>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {localErr ? (
        <div className="status-missing small" role="alert">
          {localErr}
        </div>
      ) : null}
      <LoadMoreSentinel
        hasMore={hasMore}
        loading={loading}
        onReach={() => void loadMore()}
      />
      <NewSpeechDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={() => void refresh()}
      />
    </section>
  );
}
